import React, { useState, useMemo } from 'react';
import {
  Typography,
  useTheme,
  Box,
  Stack,
  Dialog,
  MenuItem,
  Skeleton,
  styled,
  Alert,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { ATSReferenceCheckDataTestIds } from 'data-testids/ATS';
import { DEFAULT_AUTOMATION_ERROR_MESSAGE } from 'hooks/errorHandling/errorHandlingHooks';
import { PrimaryButton, SecondaryButton } from 'sharedComponents/Buttons';
import {
  primaryButtonOverrideSx,
  secondaryButtonOverrideSx,
} from 'sharedComponents/Buttons/styles';
import { ATSAutomationCreateTestIds } from 'data-testids/ATS';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { ModalPaneContainer } from 'features/Automations/styles';
import { AutomationDropdown } from 'features/Automations/CreateAutomation/components/AutomationDropdown';
import auth from 'utils/auth';
import {
  useQueryGetSuperAndDistrictAdminsData,
  useQueryGetReferenceEmailTemplateListData,
  useQueryGetReferenceTemplateListData,
} from 'features/Automations/api/listQueries';
import { useQueryGetApplicationStatusListData } from 'features/Automations/api/createQueries';
import { appStatusType } from 'utils/enums';
import {
  ReferenceCheckModalProps,
  ReferenceCheckFormData,
  ReferenceCheckSuccessData,
} from './types';
import {
  createUserOptions,
  createEmailTemplateOptions,
  createCandidateStatusOptions,
  createReferenceTemplateOptions,
  getSenderName,
  getEmailTemplateName,
  getReferenceTemplateName,
  getCandidateStatusName,
} from './utils';
import { useQueryGetAutomationsListData } from 'features/Automations/api/listQueries';
import { useAutomationMutations } from 'features/Automations/hooks/useAutomationMutations';
import { Automation } from 'features/Automations/types';
import {
  filterReferenceCheckAutomations,
  filterGeneralAutomations,
} from 'features/Automations/utils';
import { Middle } from 'features/JobClosureNotifications/styles';
import { useEmailTemplateValidation } from 'features/Automations/hooks/useEmailTemplateValidation';

interface ReferenceCheckFormProps {
  onSuccess?: (data: ReferenceCheckSuccessData) => void;
  onClose: () => void;
  jobId: number;
}

const ReferenceCheckForm: React.FC<ReferenceCheckFormProps> = ({ onSuccess, onClose, jobId }) => {
  const theme = useTheme();
  const {
    handleSubmit,
    formState: { isValid },
    watch,
  } = useFormContext<ReferenceCheckFormData>();

  const currentId = watch('id');
  const isEditing = !!currentId;
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const user = auth.getUser();
  const districtId: number | undefined = user?.profile?.district?.id;
  const { data: superAndDistrictAdmins, isLoading: isLoadingAdmins } =
    useQueryGetSuperAndDistrictAdminsData(districtId);
  const { data: emailTemplates, isLoading: isLoadingEmailTemplates } =
    useQueryGetReferenceEmailTemplateListData(user?.id);
  const { data: applicationStatuses, isLoading: isLoadingStatuses } =
    useQueryGetApplicationStatusListData(
      districtId,
      Object.values(appStatusType).filter(
        (status): status is number => typeof status === 'number' && status !== appStatusType.draft
      )
    );
  const { data: referenceTemplatesData, isLoading: isLoadingReferenceTemplates } =
    useQueryGetReferenceTemplateListData(districtId);

  const isLoadingData =
    isLoadingAdmins || isLoadingEmailTemplates || isLoadingStatuses || isLoadingReferenceTemplates;

  const userOptions = createUserOptions(superAndDistrictAdmins);

  const emailTemplateOptions = createEmailTemplateOptions(emailTemplates);

  const candidateStatusOptions = createCandidateStatusOptions(applicationStatuses);

  const referenceTemplateOptions = createReferenceTemplateOptions(referenceTemplatesData);

  const { data: existingAutomations } = useQueryGetAutomationsListData(jobId);
  const { mutateAsync, isLoading } = useAutomationMutations(jobId);

  const selectedEmailTemplateId = watch('emailTemplate');

  const selectedEmailTemplate = useMemo(() => {
    if (!selectedEmailTemplateId || !emailTemplates?.length) {
      return undefined;
    }
    return emailTemplates.find((template) => template.id === parseInt(selectedEmailTemplateId));
  }, [selectedEmailTemplateId, emailTemplates]);

  const { hasUnsupportedVariables, displayedVariables } = useEmailTemplateValidation(
    selectedEmailTemplate,
    'reference'
  );

  const onSubmit = async (data: ReferenceCheckFormData) => {
    try {
      const emailTemplateName =
        getEmailTemplateName(data.emailTemplate, emailTemplates) || data.emailTemplate;
      const referenceTemplateName =
        getReferenceTemplateName(data.referenceTemplate, referenceTemplatesData) ||
        data.referenceTemplate;
      const fromUserName = getSenderName(data.fromUser, superAndDistrictAdmins) || data.fromUser;
      const candidateStatusName =
        getCandidateStatusName(data.candidateStatus, applicationStatuses) || data.candidateStatus;

      const referenceCheckAutomation: Automation = {
        id: data.id,
        order: 0,
        trigger: {
          trigger_type: 'update_status',
          new_status_id: parseInt(data.candidateStatus),
        },
        action: {
          action_type: 'send_reference_check',
          reference_template_id: parseInt(data.referenceTemplate),
        },
        email:
          data.emailTemplate && data.fromUser
            ? {
                email_template_id: parseInt(data.emailTemplate),
                sender: parseInt(data.fromUser),
              }
            : null,
      };

      const currentAutomations = existingAutomations || [];
      const generalAutomations = filterGeneralAutomations(currentAutomations);
      const existingReferenceCheckAutomations = filterReferenceCheckAutomations(currentAutomations);

      let updatedReferenceCheckAutomations: Automation[];

      if (data.id) {
        updatedReferenceCheckAutomations = existingReferenceCheckAutomations.map((automation) =>
          automation.id === data.id ? referenceCheckAutomation : automation
        );
      } else {
        if (existingReferenceCheckAutomations.length >= 1) {
          setErrorMessage('Only one reference check automation per job is allowed');
          setShowErrorToast(true);
          return;
        }
        updatedReferenceCheckAutomations = [referenceCheckAutomation];
      }

      const updatedAutomations = [...generalAutomations, ...updatedReferenceCheckAutomations];

      await mutateAsync(updatedAutomations);

      onSuccess?.({
        id: data.id,
        emailTemplate: data.emailTemplate,
        fromUser: data.fromUser,
        referenceTemplate: data.referenceTemplate,
        candidateStatus: data.candidateStatus,
        emailTemplateName,
        referenceTemplateName,
        fromUserName,
        candidateStatusName,
      });

      onClose();
    } catch (err) {
      setErrorMessage(DEFAULT_AUTOMATION_ERROR_MESSAGE);
      setShowErrorToast(true);
    }
  };

  const handleCancel = (): void => {
    onClose();
  };

  const modalContent = (
    <Stack spacing={2}>
      {isLoadingData ? (
        <>
          <Skeleton
            variant="rectangular"
            height={50}
            data-testid={ATSAutomationCreateTestIds.AUTOMATION_SKELETON}
          />
          <Skeleton
            variant="rectangular"
            height={50}
            data-testid={ATSAutomationCreateTestIds.AUTOMATION_SKELETON}
          />
          <Skeleton
            variant="rectangular"
            height={50}
            data-testid={ATSAutomationCreateTestIds.AUTOMATION_SKELETON}
          />
          <Skeleton
            variant="rectangular"
            height={50}
            data-testid={ATSAutomationCreateTestIds.AUTOMATION_SKELETON}
          />
        </>
      ) : (
        <Middle>
          <Box>
            <Row>
              <Text variant="body1">Send email</Text>
              <AutomationDropdown
                dataTestId={ATSReferenceCheckDataTestIds.EMAIL_TEMPLATE_SELECT}
                name="emailTemplate"
                defaultValue=""
                sx={{
                  maxWidth: '60%',
                  width: '100%',
                  ...(hasUnsupportedVariables && {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.error.main,
                      borderWidth: '2px',
                    },
                  }),
                }}
              >
                {emailTemplateOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </MenuItem>
                ))}
              </AutomationDropdown>
            </Row>
            {hasUnsupportedVariables && (
              <Alert
                severity="error"
                sx={{
                  '&&': {
                    padding: '16px',
                  },
                  mt: 1,
                  mb: 2,
                  '& .MuiAlert-message': {
                    fontSize: '0.875rem',
                  },
                }}
                data-testid={ATSReferenceCheckDataTestIds.UNSUPPORTED_VARIABLES_ERROR}
              >
                Email template contains unsupported variables: {displayedVariables}
              </Alert>
            )}
          </Box>

          <Row>
            <Text variant="body1">from</Text>
            <AutomationDropdown
              dataTestId={ATSReferenceCheckDataTestIds.FROM_USER_SELECT}
              name="fromUser"
              defaultValue=""
              sx={{ maxWidth: '60%', width: '100%' }}
            >
              {userOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
          </Row>

          <Row>
            <Text variant="body1">with reference check template</Text>
            <AutomationDropdown
              dataTestId={ATSReferenceCheckDataTestIds.REFERENCE_TEMPLATE_SELECT}
              name="referenceTemplate"
              defaultValue=""
              sx={{ maxWidth: '60%', width: '100%' }}
            >
              {referenceTemplateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
          </Row>

          <Row>
            <Text variant="body1">when candidate reaches the status</Text>
            <AutomationDropdown
              dataTestId={ATSReferenceCheckDataTestIds.APPLICATION_STATUS_SELECT}
              name="candidateStatus"
              defaultValue=""
              sx={{ maxWidth: '60%', width: '100%' }}
            >
              {candidateStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
          </Row>
        </Middle>
      )}
    </Stack>
  );

  const ctaButtons = (
    <Box sx={{ pt: 3, display: 'flex', justifyContent: 'center', gap: theme.spacing(2) }}>
      <SecondaryButton
        dataTestId={ATSReferenceCheckDataTestIds.CANCEL_BUTTON}
        onClick={handleCancel}
        disabled={isLoading}
        sx={secondaryButtonOverrideSx(theme)}
      >
        Cancel
      </SecondaryButton>
      <PrimaryButton
        dataTestId={
          isEditing
            ? ATSReferenceCheckDataTestIds.SAVE_BUTTON
            : ATSReferenceCheckDataTestIds.CREATE_BUTTON
        }
        onClick={handleSubmit(onSubmit)}
        disabled={!isValid || isLoading || isLoadingData || hasUnsupportedVariables}
        sx={primaryButtonOverrideSx(theme)}
      >
        {isEditing ? 'Save' : 'Create'}
      </PrimaryButton>
    </Box>
  );

  return (
    <>
      <Typography color="black.dark" variant="h3" sx={{ textAlign: 'center' }}>
        {isEditing ? 'Edit Automated Reference Check' : 'Create Automated Reference Check'}
      </Typography>
      <ModalPaneContainer>
        {modalContent}
        {ctaButtons}
      </ModalPaneContainer>

      <ErrorSnackbar
        open={showErrorToast}
        message={errorMessage}
        onClose={() => setShowErrorToast(false)}
        dataTestId={ATSReferenceCheckDataTestIds.ERROR_TOAST}
        sx={{
          '& .MuiAlert-root': {
            padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
            borderRadius: theme.spacing(0.5),
          },
        }}
      />
    </>
  );
};

export const ReferenceCheckModal: React.FC<ReferenceCheckModalProps> = ({
  open,
  onClose,
  jobId,
  onSuccess,
  sx,
}) => {
  const theme = useTheme();

  const handleClose = (): void => {
    onClose();
  };

  return (
    <Dialog
      maxWidth={'sm'}
      fullWidth
      data-testid={ATSReferenceCheckDataTestIds.REFERENCE_CHECK_MODAL}
      onClose={handleClose}
      open={open}
      keepMounted
      sx={{
        '.MuiPaper-root': {
          borderRadius: theme.spacing(2),
          padding: 4,
        },
        ...sx,
      }}
    >
      <ReferenceCheckForm onSuccess={onSuccess} onClose={handleClose} jobId={jobId} />
    </Dialog>
  );
};

export const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  paddingBottom: theme.spacing(2),
}));

export const Text = styled(Typography)({
  width: '40%',
});
